import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import {
  DAY_OPTIONS,
  monthLabel,
  recentMonths,
  recentYears,
  sameSelection,
  selectionLabel,
} from '../../utils/analyticsRange';

/** Date range menu: recent days, lifetime, recent months and years, or custom dates. */
export default function RangePicker({ selection, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState(selection.type === 'custom' ? selection.from : '');
  const [to, setTo] = useState(selection.type === 'custom' ? selection.to : '');

  function choose(next) {
    setAnchor(null);
    onChange(next);
  }

  const option = (next, label) => (
    <MenuItem
      key={label}
      selected={sameSelection(selection, next)}
      onClick={() => choose(next)}
      sx={{ pl: 3 }}
    >
      {label}
    </MenuItem>
  );

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CalendarMonthIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ bgcolor: 'background.paper', boxShadow: 'none' }}
        aria-haspopup="menu"
      >
        {selectionLabel(selection)}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {DAY_OPTIONS.map((days) => option({ type: 'days', days }, `Last ${days} days`))}
        {option({ type: 'lifetime' }, 'Lifetime')}
        <Divider />
        <ListSubheader sx={{ lineHeight: '32px' }}>Months</ListSubheader>
        {recentMonths().map(({ year, month }) =>
          option({ type: 'month', year, month }, monthLabel(year, month)),
        )}
        <Divider />
        <ListSubheader sx={{ lineHeight: '32px' }}>Years</ListSubheader>
        {recentYears().map((year) => option({ type: 'year', year }, String(year)))}
        <Divider />
        <MenuItem
          selected={selection.type === 'custom'}
          onClick={() => {
            setAnchor(null);
            setCustomOpen(true);
          }}
          sx={{ pl: 3 }}
        >
          Custom range…
        </MenuItem>
      </Menu>
      <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Custom range</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              type="date"
              label="From"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              error={Boolean(from && to && from > to)}
              helperText={from && to && from > to ? 'Choose an end date after the start date.' : ' '}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!from || !to || from > to}
            onClick={() => {
              setCustomOpen(false);
              onChange({ type: 'custom', from, to });
            }}
          >
            Apply range
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
